import { pxToRem } from '@back-office/core';
import {
  RecurringRequestList,
  RecurringRequestListPagination,
} from '@back-office/http';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Pagination from '@material-ui/lab/Pagination';
import { lowerCase } from 'lodash';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyIcon } from '../../icons';
import { getDatetimeText } from '../../utils';
import { extractMobileNumber, getLabelFromValue } from '../../utils/general';
import GcpBox from '../gcp-box';
import GcpEnrollmentStatus, {
  EnrollmentStatus,
} from '../gcp-enrollment-status';
import GcpTypography from '../gcp-typography';
import tableHeader from './content';

const useStyles = makeStyles(
  (theme) => ({
    table: {
      minWidth: 1400,
      borderCollapse: 'separate',
    },
    headerCell: {
      padding: theme.spacing(3, 0, 1, 0),
      backgroundColor: theme.palette.common.lightGray,
      '&:first-child': {
        position: 'sticky',
        left: 0,
        width: '18%',
        boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
      },
      '&:last-child': {
        position: 'sticky',
        right: 0,
        boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
      },
    },

    bodyRow: {
      verticalAlign: 'top',
      cursor: 'pointer',
    },
    bodyCell: {
      border: 'none',
      '&:first-child': {
        position: 'sticky',
        left: 0,
        width: '18%',
        backgroundColor: 'white',
        boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
      },
      '&:last-child': {
        position: 'sticky',
        right: 0,
        backgroundColor: 'white',
        boxShadow: '0px 8px 4px 0px rgba(0,0,0,0.1);',
      },
      '& > p': {
        overflow: 'hidden',
        wordBreak: 'break-all',
      },
    },
    avatar: {
      width: pxToRem(14),
      height: pxToRem(14),
    },
    alert: {
      background: '#FFF1F0',
      border: '1px solid #FFA39E',
      boxSizing: 'border-box',
      borderRadius: '2px',
    },
    empty: {
      backgroundColor: 'white',
      '& > svg': {
        width: '10rem',
        height: '10rem',
        fill: 'none',
      },
    },
  }),
  { name: 'TransactionList' }
);

interface GcpBillerEnrollmentListTableProps {
  data: RecurringRequestList[];
  pagination: RecurringRequestListPagination;
  page: number;
  count: number;
  handleOnChangePage: (newPage: number) => void;
  handleOnClickRow: (recurringRequest: RecurringRequestList) => void;
  enrollmentTypeDropData: {
    label: string;
    value: string;
  }[];
  isEnrollmentType: boolean;
}

const GcpBillerEnrollmentListTable: FC<GcpBillerEnrollmentListTableProps> =
  observer((props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
      data,
      pagination,
      page,
      count,
      handleOnChangePage,
      handleOnClickRow,
      enrollmentTypeDropData,
      isEnrollmentType,
    } = props;

    return (
      <>
        <TableContainer component="div" style={{ maxWidth: 1060 }}>
          <Table
            aria-label="GCP Biller Enrollment List Table"
            className={classes.table}
          >
            <TableHead>
              <TableRow>
                {tableHeader
                  .filter(
                    (cell) =>
                      cell.title !==
                        'biller_enrollment_list.header.enrollment_type' ||
                      isEnrollmentType
                  )
                  .map((cell) => (
                    <TableCell
                      key={t(cell.title)}
                      align="left"
                      className={classes.headerCell}
                    >
                      <GcpBox ml={2}>
                        <GcpTypography
                          paint="mineShaft"
                          variant="body1"
                          whiteSpace="pre-line"
                        >
                          {t(cell.title)}
                        </GcpTypography>
                      </GcpBox>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data?.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  className={classes.bodyRow}
                  onClick={() => handleOnClickRow(item)}
                >
                  <TableCell className={`${classes.bodyCell}`}>
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.enrollment_id}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="9.8%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {getDatetimeText(item.created_at, 'default')}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.merchant_name}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.reference_number}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="9.5%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.wallet_id}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="11%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.full_name}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="9.5%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.created_by || '-'}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell} width="9%">
                    <GcpTypography paint="mineShaft" variant="body1">
                      {extractMobileNumber(item.mobile_no || '-')}
                    </GcpTypography>
                  </TableCell>
                  {isEnrollmentType && (
                    <TableCell className={classes.bodyCell} width="11%">
                      <GcpTypography paint="mineShaft" variant="body1">
                        {getLabelFromValue(
                          item.enrollment_type_id,
                          enrollmentTypeDropData
                        ) || '-'}
                      </GcpTypography>
                    </TableCell>
                  )}
                  <TableCell className={classes.bodyCell} width="10%">
                    <GcpBox alignItems="center" display="flex" gap={1}>
                      <GcpEnrollmentStatus
                        size={8}
                        status={
                          lowerCase(item.status || '') as EnrollmentStatus
                        }
                      />
                      <GcpTypography
                        paint="mineShaft"
                        transform="capitalize"
                        variant="body1"
                      >
                        {t(
                          `biller_enrollment_list.status.${lowerCase(
                            item.status || ''
                          )}`
                        )}
                      </GcpTypography>
                    </GcpBox>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length === 0 && (
          <GcpBox
            isFullWidth
            alignItems="center"
            className={classes.empty}
            display="flex"
            flexDirection="column"
            mt={8}
          >
            <EmptyIcon className={classes.empty} />
            <GcpTypography
              paint="hitGray"
              textAlign="center"
              variant="body1"
              whiteSpace="pre-line"
            >
              {t('biller_enrollment_list.empty')}
            </GcpTypography>
          </GcpBox>
        )}
        {data.length !== 0 && (
          <GcpBox display="flex" justifyContent="flex-end" mb={4} mt={4}>
            <GcpBox alignItems="center" display="flex" mr={2.5}>
              <GcpTypography variant="body1">
                {t('biller_enrollment_list.viewing', {
                  start: (pagination.current_page - 1) * count + 1,
                  end: Math.min(
                    pagination.current_page * count,
                    pagination.total_count
                  ),

                  total: pagination.total_count,
                })}
              </GcpTypography>
            </GcpBox>
            <Pagination
              color="primary"
              count={Math.ceil(pagination.total_count / count)}
              page={page}
              shape="rounded"
              variant="outlined"
              onChange={(_event, pageNumber) => {
                handleOnChangePage(pageNumber);
              }}
            />
          </GcpBox>
        )}
      </>
    );
  });

export default GcpBillerEnrollmentListTable;
